// Pure helpers for the new-user home. All state (steps, %, next step, home
// state) is computed server-side by GET /onboarding/status; nothing here
// re-derives it, we only shape it for display and remember a dismissal.

export type HomeState = 'new' | 'in_progress' | 'active'
export type StepTier = 'required' | 'recommended' | 'optional'

export interface OnboardingStep {
  key: string
  tier: StepTier | 'milestone'
  label: string
  description: string
  route: string
  done: boolean
}

export interface OnboardingStatus {
  home_state: HomeState
  percent: number
  completed: number
  total: number
  can_launch: boolean
  setup_complete: boolean
  campaigns_launched: number
  emails_sent: number
  next_step: Omit<OnboardingStep, 'done'> | null
  tiers: Record<StepTier, { done: number; total: number }>
  steps: OnboardingStep[]
}

export const SUPPORT_EMAIL = 'support@beevelope.com'
export const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Help getting started with Beevelope')}`
export const LAUNCH_STEP_KEY = 'launch_campaign'
export const WARMUP_TIP = 'Tip: set up warm-up first for best deliverability.'

const DISMISS_KEY_PREFIX = 'beevelope.profileCard.dismissed.'

export function stepsForTier(status: OnboardingStatus, tier: StepTier): OnboardingStep[] {
  return status.steps.filter((s) => s.tier === tier)
}

export function tierSummary(status: OnboardingStatus, tier: StepTier): string {
  const t = status.tiers[tier]
  return `${t.done} of ${t.total} done`
}

/** Steps still worth nudging on the compact profile card (recommended first). */
export function remainingSteps(status: OnboardingStatus): OnboardingStep[] {
  const rank: Record<string, number> = { required: 0, recommended: 1, optional: 2 }
  return status.steps
    .filter((s) => !s.done)
    .sort((a, b) => (rank[a.tier] ?? 9) - (rank[b.tier] ?? 9))
}

export function isLaunchHero(status: OnboardingStatus): boolean {
  return status.next_step?.key === LAUNCH_STEP_KEY
}

/** Full checklist + hero only before the account is active. */
export function showOnboardingPanel(status: OnboardingStatus): boolean {
  return status.home_state !== 'active'
}

/** "New" shows no zeroed stat cards; in_progress/active show the dashboard. */
export function showDashboardSections(status: OnboardingStatus): boolean {
  return status.home_state !== 'new'
}

/**
 * Compact profile card sits at the bottom once the account is active. It shows
 * until setup hits 100%, then collapses to a "Setup complete" strip. A user
 * dismissal hides the progress card (the completion strip still appears).
 */
export function profileCardMode(
  status: OnboardingStatus,
  dismissed: boolean,
): 'hidden' | 'progress' | 'complete' {
  if (status.home_state !== 'active') return 'hidden'
  if (status.setup_complete) return 'complete'
  return dismissed ? 'hidden' : 'progress'
}

export function isProfileCardDismissed(userId: number | string | undefined): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY_PREFIX + (userId ?? 'anon')) === '1'
  } catch {
    return false
  }
}

export function dismissProfileCard(userId: number | string | undefined): void {
  try {
    window.localStorage.setItem(DISMISS_KEY_PREFIX + (userId ?? 'anon'), '1')
  } catch {
    /* storage unavailable: the dismissal just does not persist */
  }
}
